/**
 * Datadogログフィルタリングユーティリティ
 *
 * SDKやネイティブプラグインの仕様上、避けられないノイズログを
 * カテゴリごとに管理してDatadogへの送信を抑制する。
 *
 * 新しいカテゴリを追加する場合は LOG_FILTER_CATEGORIES に追加する。
 */

interface LogFilterCategory {
    /** カテゴリ名 */
    category: string
    /** フィルタするメッセージのパターン一覧 */
    patterns: string[]
}

/**
 * フィルタリング対象のカテゴリ一覧
 * 新しいSDKやサービス固有のノイズが増えた場合はここに追加する。
 */
const LOG_FILTER_CATEGORIES: LogFilterCategory[] = [
    {
        category: "Capgo (CapacitorUpdater)",
        patterns: [
            // 最新バンドルを使用中の場合に Capgo SDK が error レベルで出力する仕様上のログ。
            // 新バージョンがないことは正常状態のため除外。（既存対応: PR #2406, 2026-03-10）
            "no_new_version_available",

            // 低速デバイスや不安定なネットワーク環境で Capgo 内部の semaphore がタイムアウトした際に発生。
            // appReadyTimeout (20秒) を超える初期化や、更新確認・チャンネル取得などの内部オペレーションで起きる。
            // ユーザーの通信/デバイス環境に依存するため根本解決困難。
            "Semaphore wait timed out",

            // Capgo サーバーへの最新バンドル確認リクエストがネットワークエラーで失敗した場合に発生。
            // ユーザーのオフライン状態や不安定な通信環境が原因。アプリ動作には影響しない。
            "getLatest failed with error: network_error",

            // 最新バンドルのバージョン取得に失敗した場合に発生。ネットワーク起因。
            // 次回アプリ起動時に再試行されるため致命的ではない。
            "Error getting latest version",

            // Capgo チャンネル情報の取得に失敗した場合に発生。ネットワーク起因の一時的なエラー。
            // チャンネル設定は capacitor.config.ts で静的に設定されているため機能に影響しない。
            "Error getting channel",

            // Capgo への統計バッチ送信が失敗した場合に発生。ネットワーク起因。
            // 統計送信の失敗はアプリの更新動作に影響しない。
            // SDK バージョンによってメッセージが異なる場合があるため両方を対象とする。
            "Error sending stats batch",
            "Failed to send stats batch",

            // ローカルに保存されたバンドル情報のパースに失敗した場合に発生。
            // データ破損またはプラグインバージョンアップによる形式不一致が原因の可能性。
            // 次回の Live Update で自動的に上書きされるため一時的なエラー。
            "Failed to parse bundle info"
        ]
    }
    // 将来的に他のカテゴリを追加する場合はここに追記する
    // 例:
    // {
    //     category: "ServiceWorker",
    //     patterns: [ ... ],
    // },
]

/**
 * 指定したログメッセージを Datadog へ送信すべきか判定する。
 *
 * @param message - datadogLogs の beforeSend で受け取るログメッセージ
 * @returns true: 抑制する（送信しない） / false: 送信する
 */
export function shouldSuppressLog(message: string | undefined): boolean {
    if (!message) return false
    return LOG_FILTER_CATEGORIES.some(({ patterns }) =>
        patterns.some(pattern => message.includes(pattern))
    )
}
